// The open Design document: model + history + view, and autosave (DRAW_PLAN §1, §4). Documents are saved as
// designs/<name>.epd in the open Photo Booth folder through the guarded writer, or in IndexedDB when no
// folder is open. Autosave runs 1.5 s after the last edit.
import { create } from 'zustand';
import { emptyHistory, execute, redo, seal, undo, type Command, type History } from './commands/history';
import { newDoc } from './model/defaults';
import { readEpd, writeEpd } from './model/epd';
import type { Doc } from './model/types';
import { useLibrary } from '../state/library';
import { listDesignsIdb, readDesignIdb, writeDesignIdb } from '../io/fs';
import { loadAssets, referencedAssets } from './assets';

export type View = { zoom: number; panX: number; panY: number };
export type SaveState = 'saved' | 'dirty' | 'saving' | 'error';

type DesignState = {
  doc: Doc | null;
  /** The file the document lives in (designs/<file>.epd), fixed when created or opened: renaming the
   *  document changes its title, not its file, so no copy is left behind under the old name. */
  file: string | null;
  history: History;
  view: View;
  save: SaveState;
  saveError: string | null;
  /** Bumped on every change, so canvases know when to redraw. */
  version: number;
  run: (c: Command) => void;
  endGesture: () => void;
  undo: () => void;
  redo: () => void;
  setView: (v: Partial<View>) => void;
  create: (presetId: string, name: string, file: string) => void;
  /** A new document made elsewhere (an ad from a template); saved at once. */
  createFrom: (doc: Doc, file: string) => void;
  load: (doc: Doc, file: string) => void;
  close: () => void;
};

const LAST_KEY = 'epb.design.last';
const SAVE_DELAY = 1500;
let timer: number | undefined;

function schedule() {
  clearTimeout(timer);
  timer = window.setTimeout(() => void saveDesignNow(), SAVE_DELAY);
}

export const useDesign = create<DesignState>((set, get) => ({
  doc: null,
  file: null,
  history: emptyHistory(),
  view: { zoom: 0, panX: 0, panY: 0 }, // zoom 0 = not fitted yet
  save: 'saved',
  saveError: null,
  version: 0,
  run: (c) => {
    const { doc, history, version } = get();
    if (!doc) return;
    const r = execute(doc, history, c);
    if (r.doc === doc) return;
    set({ doc: r.doc, history: r.history, version: version + 1, save: 'dirty' });
    schedule();
  },
  // Also a redraw: a drag that floated objects above the tiles puts them back in.
  endGesture: () => set({ history: seal(get().history), version: get().version + 1 }),
  undo: () => {
    const { doc, history, version } = get();
    if (!doc || !history.past.length) return;
    const r = undo(doc, history);
    set({ doc: r.doc, history: r.history, version: version + 1, save: 'dirty' });
    schedule();
  },
  redo: () => {
    const { doc, history, version } = get();
    if (!doc || !history.future.length) return;
    const r = redo(doc, history);
    set({ doc: r.doc, history: r.history, version: version + 1, save: 'dirty' });
    schedule();
  },
  setView: (v) => set({ view: { ...get().view, ...v }, version: get().version + 1 }),
  create: (presetId, name, file) => {
    const doc = newDoc(presetId, name);
    set({
      doc,
      file,
      history: emptyHistory(),
      view: { zoom: 0, panX: 0, panY: 0 },
      save: 'dirty',
      version: get().version + 1,
    });
    remember(file);
    void saveDesignNow();
  },
  createFrom: (doc, file) => {
    set({
      doc,
      file,
      history: emptyHistory(),
      view: { zoom: 0, panX: 0, panY: 0 },
      save: 'dirty',
      version: get().version + 1,
    });
    remember(file);
    void saveDesignNow();
  },
  load: (doc, file) => {
    set({
      doc,
      file,
      history: emptyHistory(),
      view: { zoom: 0, panX: 0, panY: 0 },
      save: 'saved',
      version: get().version + 1,
    });
    remember(file);
  },
  close: () => {
    clearTimeout(timer);
    set({ doc: null, file: null, history: emptyHistory() });
  },
}));

function remember(name: string) {
  try {
    localStorage.setItem(LAST_KEY, name);
  } catch {
    /* not remembered */
  }
}
export function lastDesignName(): string | null {
  try {
    return localStorage.getItem(LAST_KEY);
  } catch {
    return null;
  }
}

/** File-safe document name (the document's own name may hold anything). */
export const designFileName = (name: string) =>
  [...name]
    .map((c) => (c.charCodeAt(0) < 32 || '\\/:*?"<>|'.includes(c) ? '_' : c))
    .join('')
    .replace(/^\.+/, '')
    .trim()
    .slice(0, 120) || 'Untitled';

export async function saveDesignNow(): Promise<void> {
  clearTimeout(timer);
  const { doc } = useDesign.getState();
  if (!doc) return;
  useDesign.setState({ save: 'saving' });
  try {
    // M14: photos and the logo travel inside the file (assets/<hash>.<ext>).
    const bytes = writeEpd(doc, referencedAssets(doc));
    const folder = useLibrary.getState().folder;
    const file = useDesign.getState().file ?? designFileName(doc.name);
    if (folder) await folder.writeDesign(file, bytes);
    else await writeDesignIdb(file, bytes);
    if (useDesign.getState().doc === doc) useDesign.setState({ save: 'saved', saveError: null });
  } catch (e) {
    useDesign.setState({ save: 'error', saveError: (e as Error).message });
  }
}

export async function listDesigns(): Promise<string[]> {
  const folder = useLibrary.getState().folder;
  return folder ? folder.listDesigns() : listDesignsIdb();
}

export async function openDesign(name: string): Promise<boolean> {
  const folder = useLibrary.getState().folder;
  const bytes = folder ? await folder.readDesign(name) : await readDesignIdb(name);
  if (!bytes) return false;
  const r = readEpd(bytes);
  loadAssets(r.assets);
  useDesign.getState().load(r.doc, name);
  return true;
}

/** A new document in a new file: never reuses an existing design's file name ("Menu", "Menu 2", …). */
export async function createDesign(presetId: string, name: string): Promise<void> {
  useDesign.getState().create(presetId, name, await freeFileName(name));
}

/** A design file name not used yet ("Menu", "Menu 2", …). */
export async function freeFileName(name: string): Promise<string> {
  const taken = new Set((await listDesigns()).map((n) => n.toLowerCase()));
  const base = designFileName(name);
  let file = base;
  for (let k = 2; taken.has(file.toLowerCase()); k++) file = `${base} ${k}`;
  return file;
}

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => void (useDesign.getState().save === 'dirty' && saveDesignNow()));
}
